import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClaimDataEntriesFragmentFragment,
  ScheduledActionFragmentFragment,
  useClaimScheduledActionsQuery,
} from '../../../../api/gql';
import { flattenObject } from '../../../../utils/claim';
import { getPriceWithCurrencyString } from '../../../../utils/currencies';
import { fromCentesimal } from '../../../../utils/transform';

const TRANS_NS = '';
const EntriesKeysToOmit = ['claim.currency', 'claim.bankNumber'];
export const useClaimDataOverview = ({
  transNS = TRANS_NS,
  data,
  scheduledActionsData,
}: {
  id: string;
  transNS?: string;
  data?: ClaimDataEntriesFragmentFragment;
  scheduledActionsData?: ScheduledActionFragmentFragment[];
}) => {
  const { t } = useTranslation(transNS);

  const firstScheduledAction = useMemo(
    () => scheduledActionsData?.sort((a, b) => a.scheduledAt - b.scheduledAt)?.shift(),
    [scheduledActionsData],
  );

  const dataEntries = useMemo((): { [key: string]: string | number } | null => {
    if (!data) return null;
    const flatten = flattenObject(data, 'claim');

    // entries that needs additional transformations
    const defaultEntries = {
      'claim.amountDue': getPriceWithCurrencyString(fromCentesimal(data.amountDue ?? 0), data.currency),
      'claim.dueDate': data.dueDate ? new Date(data.dueDate).toLocaleDateString() : '',
      'claim.remindingStart': firstScheduledAction?.scheduledAt
        ? new Date(firstScheduledAction.scheduledAt).toLocaleDateString()
        : '',
      'claim.bankAccount': data.bankAccount && data.bankNumber ? `${data.bankAccount}/${data.bankNumber}` : '',
    };

    const entries = {} as { [key: string]: string | number };

    Object.entries({
      ...flatten,
      ...defaultEntries,
    })
      .filter(
        ([key, value]) => value !== null && value !== undefined && value !== '' && !EntriesKeysToOmit.includes(key),
      )
      .forEach(([key, value]) => {
        entries[t(key)] = value;
      });

    return entries;
  }, [data, firstScheduledAction?.scheduledAt, t]);

  return {
    entries: dataEntries,
  };
};
